import { BoardMeetingState } from "@boark/shared/types/board-meeting";
import { BoardMeetingCommentWithCreatedBy } from "@boark/shared/types/board-meeting-comment-with-created-by";
import {
  BoardMeetingContent,
  BoardMeetingContentState,
  BoardMeetingContentType,
  BoardMeetingDecision,
} from "@boark/shared/types/board-meeting-content";
import { BoardMeetingContentUpdate } from "@boark/shared/types/board-meeting-content-update";
import { BoardMeetingWithDetails } from "@boark/shared/types/board-meeting-with-details";
import {
  canCommentOnBoardMeeting,
  canEditBoardMeetingAgenda,
  canEditBoardMeetingConclusions,
  getBoardMeetingContentCloseError,
  hasBoardMeetingEndedSnapshot,
} from "@boark/shared/utils/board-meeting";
import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronRight,
  faLock,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";

import { useBoardMeetingMutation } from "../queries/board-meeting";
import { useBlockedReason } from "../services/board-meeting-locale";
import {
  deleteBoardMeetingContent,
  updateBoardMeetingContent,
} from "../services/board-meeting";
import { useLocale } from "../stores/locale";
import { BoardMeetingContentComments } from "./board-meeting-content-comments";
import { BoardMeetingContentFiles } from "./board-meeting-content-files";
import { Button } from "./button";
import { Confirm } from "./confirm";
import { IconButton } from "./icon-button";
import { Select } from "./select";

const textareaClassName =
  "w-full border-1 border-gray-300 rounded-xs p-2 min-h-[4.5rem] bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed";

const CONCLUSION_VISIBLE_STATES = [
  BoardMeetingState.ONGOING,
  BoardMeetingState.ENDED,
  BoardMeetingState.CLEANING,
  BoardMeetingState.REVIEW,
  BoardMeetingState.COMPLETE,
];

export function BoardMeetingContentItem({
  meeting,
  content,
  number,
  authUserId,
  comments,
  isFirst,
  isLast,
  onMove,
}: {
  meeting: BoardMeetingWithDetails;
  content: BoardMeetingContent;
  number: string;
  authUserId?: string;
  comments: BoardMeetingCommentWithCreatedBy[];
  isFirst: boolean;
  isLast: boolean;
  onMove: (direction: -1 | 1) => void;
}) {
  const { locale: t } = useLocale();
  const blockedReason = useBlockedReason();
  const [statement, setStatement] = useServerDraft(content.statement);
  const [conclusion, setConclusion] = useServerDraft(content.conclusion ?? "");
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const update = useBoardMeetingMutation(
    meeting.id,
    (changes: BoardMeetingContentUpdate) =>
      updateBoardMeetingContent(meeting.id, content.id, changes),
  );
  const remove = useBoardMeetingMutation(meeting.id, () =>
    deleteBoardMeetingContent(meeting.id, content.id),
  );

  const agendaEditable = canEditBoardMeetingAgenda(meeting, authUserId);
  const conclusionsEditable = canEditBoardMeetingConclusions(
    meeting,
    authUserId,
  );
  const isClosed = content.state === BoardMeetingContentState.CLOSED;
  const conclusionEditable = conclusionsEditable && !isClosed;
  const showConclusion = CONCLUSION_VISIBLE_STATES.includes(meeting.state);
  const isDecision = content.type === BoardMeetingContentType.DECISION;
  const closeError = getBoardMeetingContentCloseError(content);

  const snapshotDiffers =
    hasBoardMeetingEndedSnapshot(meeting.state) &&
    ((content.endedConclusion ?? "") !== (content.conclusion ?? "") ||
      content.endedDecision !== content.decision);

  const conclusionLabel = isDecision
    ? t.boardMeeting.conclusion
    : t.boardMeeting.conclusionInformation;

  const saveStatement = () => {
    if (statement !== content.statement) {
      update.mutate({ statement });
    }
  };

  const conclusionChanged = conclusion !== (content.conclusion ?? "");
  const conclusionPayload = {
    conclusion: conclusion === "" ? null : conclusion,
  };

  const saveConclusion = () => {
    if (conclusionChanged) {
      update.mutate(conclusionPayload);
    }
  };

  return (
    <article
      className={`flex flex-col gap-3 border rounded-xs p-3 ${
        isClosed && showConclusion
          ? "border-gray-200 bg-gray-50"
          : "border-gray-300 bg-white"
      }`}
    >
      <header className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-gray-700">{number}</span>
        {agendaEditable ? (
          <Select
            className="w-40"
            options={Object.values(BoardMeetingContentType).map((type) => ({
              label: t.boardMeeting.contentTypes[type],
              value: type,
            }))}
            value={content.type}
            onChange={(type) =>
              update.mutate({ type: type as BoardMeetingContentType })
            }
          />
        ) : (
          <TypeBadge type={content.type} />
        )}
        {showConclusion && (
          <span
            className={`text-xs rounded-full px-2 py-0.5 ${
              isClosed
                ? "bg-gray-200 text-gray-700"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            {isClosed && <FontAwesomeIcon className="mr-1" icon={faLock} />}
            {t.boardMeeting.contentStates[content.state]}
          </span>
        )}
        <div className="flex-1" />
        {update.isPending && (
          <span className="text-xs text-gray-500">{t.boardMeeting.saving}</span>
        )}
        {agendaEditable && (
          <div className="flex gap-1">
            <IconButton
              disabled={isFirst}
              icon={faArrowUp}
              title={t.boardMeeting.moveUp}
              onClick={() => onMove(-1)}
            />
            <IconButton
              disabled={isLast}
              icon={faArrowDown}
              title={t.boardMeeting.moveDown}
              onClick={() => onMove(1)}
            />
            <IconButton
              icon={faTrash}
              title={t.boardMeeting.delete}
              onClick={() => setDeleteConfirmOpen(true)}
            />
          </div>
        )}
      </header>

      <div>
        <h4 className="text-sm font-medium text-gray-500 m-0 mb-1">
          {t.boardMeeting.statement}
        </h4>
        {agendaEditable ? (
          <textarea
            className={textareaClassName}
            placeholder={t.boardMeeting.statementPlaceholder}
            value={statement}
            onBlur={saveStatement}
            onChange={(e) => setStatement(e.target.value)}
          />
        ) : (
          <p className="m-0 whitespace-pre-wrap">
            {content.statement || (
              <span className="text-gray-400">
                {t.boardMeeting.statementEmpty}
              </span>
            )}
          </p>
        )}
      </div>

      <BoardMeetingContentFiles
        contentId={content.id}
        meetingId={meeting.id}
        readOnly={!agendaEditable}
      />

      {showConclusion && (
        <div className="flex flex-col gap-2 border-t border-gray-200 pt-3">
          {snapshotDiffers && (
            <ConclusionView
              className="bg-amber-50 border border-amber-200 rounded-xs p-2"
              conclusion={content.endedConclusion}
              decision={isDecision ? content.endedDecision : undefined}
              label={`${conclusionLabel} – ${t.boardMeeting.notedDuringMeeting}`}
            />
          )}
          {conclusionEditable ? (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-500">
                {snapshotDiffers || meeting.state === BoardMeetingState.CLEANING
                  ? `${conclusionLabel} – ${t.boardMeeting.cleanedMinutes}`
                  : conclusionLabel}
              </label>
              <textarea
                className={textareaClassName}
                placeholder={t.boardMeeting.conclusionPlaceholder}
                value={conclusion}
                onBlur={saveConclusion}
                onChange={(e) => setConclusion(e.target.value)}
              />
              {isDecision && (
                <Select
                  allowEmpty
                  className="sm:w-60"
                  label={t.boardMeeting.decision}
                  options={Object.values(BoardMeetingDecision).map(
                    (decision) => ({
                      label: t.boardMeeting.decisions[decision],
                      value: decision,
                    }),
                  )}
                  placeholder={t.boardMeeting.decisionNone}
                  value={content.decision ?? ""}
                  onChange={(decision) =>
                    update.mutate({
                      decision:
                        decision === ""
                          ? null
                          : (decision as BoardMeetingDecision),
                    })
                  }
                />
              )}
            </div>
          ) : (
            <ConclusionView
              conclusion={content.conclusion}
              decision={isDecision ? content.decision : undefined}
              label={
                snapshotDiffers
                  ? `${conclusionLabel} – ${t.boardMeeting.cleanedMinutes}`
                  : conclusionLabel
              }
              showDecisionEmpty={isDecision}
            />
          )}
          {conclusionsEditable && (
            <div className="flex justify-end">
              {isClosed ? (
                <Button
                  severity="secondary"
                  size="sm"
                  onClick={() =>
                    update.mutate({ state: BoardMeetingContentState.OPEN })
                  }
                >
                  {t.boardMeeting.reopen}
                </Button>
              ) : (
                <Button
                  disabled={closeError !== undefined}
                  size="sm"
                  title={closeError ? blockedReason(closeError) : undefined}
                  onClick={() =>
                    // Send an unsaved conclusion along so it isn't lost
                    update.mutate({
                      ...(conclusionChanged ? conclusionPayload : {}),
                      state: BoardMeetingContentState.CLOSED,
                    })
                  }
                >
                  <FontAwesomeIcon className="mr-1" icon={faLock} />
                  {t.boardMeeting.close}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="border-t border-gray-200 pt-2">
        <button
          className="text-sm text-gray-600 cursor-pointer"
          type="button"
          onClick={() => setCommentsOpen((value) => !value)}
        >
          <FontAwesomeIcon
            className="mr-1"
            icon={commentsOpen ? faChevronDown : faChevronRight}
          />
          {t.boardMeeting.commentsCount.replace(
            "{count}",
            String(comments.length),
          )}
        </button>
        {commentsOpen && (
          <div className="mt-2">
            <BoardMeetingContentComments
              authUserId={authUserId}
              canComment={canCommentOnBoardMeeting(meeting, authUserId)}
              comments={comments}
              contentId={content.id}
              meetingId={meeting.id}
            />
          </div>
        )}
      </div>

      {deleteConfirmOpen && (
        <Confirm
          header={t.boardMeeting.contentConfirmDelete}
          loading={remove.isPending}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={() => {
            void (async () => {
              await remove.mutateAsync(undefined);
              setDeleteConfirmOpen(false);
            })();
          }}
        />
      )}
    </article>
  );
}

// Local text for a field saved on blur. Takes in new values from the server
// only while there are no unsaved local edits.
function useServerDraft(
  serverValue: string,
): [string, (value: string) => void] {
  const [draft, setDraft] = useState(serverValue);
  const synced = useRef(serverValue);

  useEffect(() => {
    setDraft((current) => (current === synced.current ? serverValue : current));
    synced.current = serverValue;
  }, [serverValue]);

  return [draft, setDraft];
}

function ConclusionView({
  label,
  conclusion,
  decision,
  showDecisionEmpty = false,
  className = "",
}: {
  label: string;
  conclusion?: string;
  decision?: BoardMeetingDecision;
  showDecisionEmpty?: boolean;
  className?: string;
}) {
  const { locale: t } = useLocale();

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-500 m-0 mb-1">{label}</h4>
      {(decision || showDecisionEmpty) && (
        <p className="m-0 mb-1">
          <DecisionBadge decision={decision} />
        </p>
      )}
      <p className="m-0 whitespace-pre-wrap">
        {conclusion || (
          <span className="text-gray-400">
            {t.boardMeeting.conclusionEmpty}
          </span>
        )}
      </p>
    </div>
  );
}

function TypeBadge({ type }: { type: BoardMeetingContentType }) {
  const { locale: t } = useLocale();
  return (
    <span
      className={`text-xs rounded-full px-2 py-0.5 ${
        type === BoardMeetingContentType.DECISION
          ? "bg-blue-100 text-blue-800"
          : "bg-gray-100 text-gray-700"
      }`}
    >
      {t.boardMeeting.contentTypes[type]}
    </span>
  );
}

function DecisionBadge({ decision }: { decision?: BoardMeetingDecision }) {
  const { locale: t } = useLocale();
  const colors: Record<BoardMeetingDecision, string> = {
    [BoardMeetingDecision.RESOLVED]: "bg-green-100 text-green-800",
    [BoardMeetingDecision.TABLED]: "bg-amber-100 text-amber-800",
    [BoardMeetingDecision.DISMISSED]: "bg-red-100 text-red-800",
  };
  return (
    <span
      className={`text-xs font-medium rounded-full px-2 py-0.5 ${
        decision ? colors[decision] : "bg-gray-100 text-gray-500"
      }`}
    >
      {decision
        ? t.boardMeeting.decisions[decision]
        : t.boardMeeting.decisionNone}
    </span>
  );
}
